import * as moment from 'moment-timezone';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Challenge, ChallengeService } from 'app/shared/services/challenge.service';
import { User } from 'app/shared/models/User';
import { Subject } from 'rxjs';
import { UserService } from 'app/shared/services/user.service';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';

/**
 * Statistics section for challenges, containing information about challenge.
 * Challenge is either displayed as future, running or past. Past challenge is displayed for 14 days after challenge concludes.
 */
@Component({
  selector: 'app-challenge-statistics',
  templateUrl: './challenge-statistics.component.html',
  styleUrls: ['./challenge-statistics.component.scss'],
})
export class ChallengeStatisticsComponent implements OnInit, OnDestroy {
  // Only one challenge is displayed (Since we assume only one is going to exist at the same time anyway)
  public displayedChallenge: Challenge;

  // Displayed timeframe of team challenge
  public displayedTimeframe: string;

  // The currently logged in user
  public currentUser: User;

  // Whether the challenge lies in the future and therefore has to be displayed differently
  public isFutureChallenge: boolean;

  // Challenge id from query param which was requested by user
  private requestedChallengeID: string;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private challengeService: ChallengeService,
    private userService: UserService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.challengeService.displayedChallenge.takeUntil(this.ngUnsubscribe).subscribe((challenge) => {
      if (challenge) {
        this.displayedChallenge = challenge;
        this.isFutureChallenge = moment(challenge.start).isAfter(moment());
        this.setDisplayedTimeframe();
      } else {
        this.displayedChallenge = null;
        this.isFutureChallenge = false;
      }

      if (this.requestedChallengeID && (!challenge || challenge._id !== this.requestedChallengeID)) {
        this.snackBar.open(this.translate.instant('CHALLENGE.error.challengeIsPast'), null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
      }
    });

    this.route.queryParams.takeUntil(this.ngUnsubscribe).subscribe((params) => {
      if (params.challengeID) {
        this.requestedChallengeID = params.challengeID;
      }
    });

    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.setDisplayedTimeframe();
      }
    });
  }

  private setDisplayedTimeframe() {
    if (!this.displayedChallenge || !this.currentUser) {
      return;
    }

    const language = this.currentUser.language;

    moment.locale('de');
    if (language === 'en') {
      moment.locale('en');
    }

    this.displayedTimeframe =
      moment(this.displayedChallenge.start).tz(this.currentUser.timeZone).format('L') +
      ' - ' +
      moment(this.displayedChallenge.end).tz(this.currentUser.timeZone).format('L');
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/theme/components/challenge-statistics/challenge-statistics.component.ts